import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Box, Collapse, Divider, Paper, Stack, SvgIcon, Typography } from '@mui/material'
import { FC, useRef, useState } from 'react'
import { Location, LocationAddressPropertyType } from '../../api/models'
import ApartmentIcon from '../../assets/images/apartment.svg?react'
import BusinessIcon from '../../assets/images/business.svg?react'
import NoPropertyTypeIcon from '../../assets/images/no-property-type-icon.svg?react'
import HouseIcon from '../../assets/images/house.svg?react'
import NoPropertyIcon from '../../assets/images/no_property.svg?react'
import { MISSING_VALUE } from '../../constants'
import { getSupportHubUrl, getPropertyType } from '../../utils/permitUtils'
import { LocationDetails } from './LocationDetails'

interface LocationCardProps {
  location?: Location
  microView?: boolean
  onMouseEnter?: () => void
  onMouseLeave?: () => void
}

export const LocationCard: FC<LocationCardProps> = ({ location, microView = false, onMouseEnter, onMouseLeave }) => {
  const [expanded, setExpanded] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)

  const handleToggleExpand = () => {
    if (!microView) {
      setExpanded(!expanded)
    }
  }

  const getIcon = () => {
    const createSvgIcon = (iconProps: { component: FC; testId: string }) => (
      <SvgIcon component={iconProps.component} data-testid={iconProps.testId} inheritViewBox sx={{ flexShrink: 0 }} />
    )

    if (!location) {
      return createSvgIcon({ component: NoPropertyIcon, testId: 'location-card-icon-no-property' })
    }

    switch (location.address?.propertyType) {
      case LocationAddressPropertyType.House:
        return createSvgIcon({ component: HouseIcon, testId: 'location-card-icon-house' })
      case LocationAddressPropertyType.Commercial:
        return createSvgIcon({ component: BusinessIcon, testId: 'location-card-icon-business' })
      case LocationAddressPropertyType.Apartment:
        return createSvgIcon({ component: ApartmentIcon, testId: 'location-card-icon-apartment' })
      default:
        return createSvgIcon({ component: NoPropertyTypeIcon, testId: 'location-card-icon-default' })
    }
  }

  const withLocationBg = `var(--bg-surface-high)`
  const withoutLocationBg = `var(--bg-surface-medium)`

  if (microView) {
    return (
      <Paper
        ref={cardRef}
        data-testid="location-card-micro-view"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        sx={{
          blockSize: '3.75rem',
          width: '100%',
          position: 'relative',
          bgcolor: location ? withLocationBg : withoutLocationBg,
          borderRadius: 'var(--border-radius-s)',
          overflow: 'hidden',
          padding: 'var(--spacing-2xs) var(--spacing-3xs)',
          textAlign: 'center',
          cursor: 'pointer',
          color: 'var(--text-inverted)'
        }}
      >
        <Stack spacing="var(--spacing-2xs)" alignItems="center" justifyContent="center" useFlexGap>
          {getIcon()}
          <Typography
            component="span"
            data-testid="location-card-micro-street"
            sx={{
              fontSize: 'var(--size-2xs)',
              fontWeight: 600,
              color: 'var(--text-inverted)',
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {location ? location.address?.street1 : MISSING_VALUE}
          </Typography>
        </Stack>
      </Paper>
    )
  }

  if (!location) {
    return (
      <Paper
        data-testid="location-card-no-location-container"
        sx={{
          blockSize: '3.75rem',
          width: '100%',
          bgcolor: withoutLocationBg,
          borderRadius: 'var(--border-radius-s)',
          overflow: 'hidden',
          padding: 'var(--spacing-2xs) var(--spacing-m) var(--spacing-2xs) var(--spacing-s)',
          color: 'var(--text-primary)'
        }}
      >
        <Stack direction="row" alignItems="center" spacing="var(--spacing-s)" useFlexGap>
          {getIcon()}
          <Stack
            sx={{
              minInlineSize: 0
            }}
          >
            <Typography
              component="span"
              data-testid="location-card-no-location-text"
              sx={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              No Location Selected
            </Typography>
            <Typography
              component="span"
              data-testid="location-card-no-location-subtext"
              sx={{
                fontSize: 'var(--size-2xs)',
                fontWeight: 300,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              -
            </Typography>
          </Stack>
        </Stack>
      </Paper>
    )
  }

  return (
    <Paper
      ref={cardRef}
      data-testid="location-card-regular-view"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sx={{
        blockSize: expanded ? undefined : '3.75rem',
        width: '100%',
        position: 'relative',
        bgcolor: withLocationBg,
        borderRadius: 'var(--border-radius-s)',
        overflow: 'hidden',
        paddingTop: 'var(--spacing-2xs)',
        paddingRight: 'var(--spacing-m)',
        paddingBottom: 'var(--spacing-2xs)',
        paddingLeft: 'var(--spacing-s)',
        color: 'var(--text-inverted)'
      }}
    >
      <Stack
        direction="row"
        data-testid="location-card-header"
        spacing="var(--spacing-s)"
        onClick={handleToggleExpand}
        useFlexGap
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer'
        }}
      >
        <Stack
          direction="row"
          spacing="var(--spacing-s)"
          useFlexGap
          sx={{
            alignItems: 'center',
            minInlineSize: 0
          }}
        >
          {getIcon()}
          <Stack
            sx={{
              minInlineSize: 0
            }}
          >
            <Typography
              component="span"
              data-testid="location-card-street"
              sx={{
                fontWeight: 700,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {location.address?.street1}
            </Typography>
            <Typography
              component="span"
              data-testid="location-card-city-state-zip"
              sx={{
                fontSize: 'var(--size-2xs)',
                fontWeight: 300,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {`${location.address?.city || ''}, ${location.address?.state || ''} ${location.address?.zip || ''}`}
            </Typography>
          </Stack>
        </Stack>
        {expanded ? <ExpandLessIcon data-testid="location-card-expand-less" /> : <ExpandMoreIcon data-testid="location-card-expand-more" />}
      </Stack>

      <Collapse in={expanded} data-testid="location-card-collapse">
        <Box onClick={e => e.stopPropagation()}>
          <Divider sx={{ bgcolor: 'var(--shape-info-var)', my: 1 }} />
          <LocationDetails location={location} propertyType={getPropertyType(location)} supportHubUrl={getSupportHubUrl(location)} />
        </Box>
      </Collapse>
    </Paper>
  )
}
